import { FEMachine } from '@/types/flexe';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { MoreHorizontal, Pencil, Trash2 } from 'lucide-react';
import { toKW } from '@/lib/flexeScenarioCalculations';
import { MachineBadge } from './MachineBadge';
import { ScheduleVisualizer } from './ScheduleVisualizer';
import { useState } from 'react';

interface MachinesTableProps {
  machines: FEMachine[];
  searchQuery: string;
  onEdit: (machine: FEMachine) => void;
  onDelete: (machineId: string) => void;
  isDeleting: boolean;
}

export function MachinesTable({
  machines,
  searchQuery,
  onEdit,
  onDelete,
  isDeleting,
}: MachinesTableProps) {
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  const handleDeleteClick = (machineId: string) => {
    setDeleteConfirm(machineId);
  };

  const handleDeleteConfirm = () => {
    if (deleteConfirm) {
      onDelete(deleteConfirm);
      setDeleteConfirm(null);
    }
  };

  const getPowerDisplay = (machine: FEMachine) => {
    if (machine.kw) {
      return `${machine.kw} kW`;
    }
    const calculated = toKW(machine.amps || 0, machine.cosphi, machine.phases, machine.voltage);
    return `~${calculated.toFixed(1)} kW`;
  };

  const getScheduleDisplay = (schedule: Record<string, string[][]>) => {
    if (!schedule || Object.keys(schedule).length === 0) {
      return '24/7';
    }
    const days = Object.keys(schedule);
    if (days.length === 5 && days.every(d => ['mon', 'tue', 'wed', 'thu', 'fri'].includes(d))) {
      const slots = schedule[days[0]];
      if (slots?.length === 1 && slots[0][0] === '09:00' && slots[0][1] === '17:00') {
        return 'Ma-Vr 09-17';
      }
    }
    return 'Custom';
  };

  return (
    <>
      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Naam</TableHead>
              <TableHead>Vermogen</TableHead>
              <TableHead>Duty / Concurrency</TableHead>
              <TableHead>Schedule</TableHead>
              <TableHead>Units</TableHead>
              <TableHead className="w-[100px]">Acties</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {machines.map((machine) => (
              <TableRow key={machine.id} className="cursor-pointer hover:bg-muted/50" onClick={() => onEdit(machine)}>
                <TableCell className="font-medium">
                  <MachineBadge machine={machine} />
                </TableCell>
                <TableCell>{getPowerDisplay(machine)}</TableCell>
                <TableCell>
                  <div className="flex flex-col text-sm">
                    <span>{(machine.duty_cycle * 100).toFixed(0)}% duty</span>
                    <span className="text-muted-foreground">{(machine.concurrency_factor * 100).toFixed(0)}% conc.</span>
                  </div>
                </TableCell>
                <TableCell>
                  <ScheduleVisualizer schedule={machine.schedule} compact />
                </TableCell>
                <TableCell>{machine.units}</TableCell>
                <TableCell onClick={(e) => e.stopPropagation()}>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon">
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => onEdit(machine)}>
                        <Pencil className="mr-2 h-4 w-4" />
                        Bewerken
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        className="text-destructive"
                        onClick={() => handleDeleteClick(machine.id)}
                        disabled={isDeleting}
                      >
                        <Trash2 className="mr-2 h-4 w-4" />
                        Verwijderen
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <AlertDialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Machine verwijderen?</AlertDialogTitle>
            <AlertDialogDescription>
              Deze actie kan niet ongedaan worden gemaakt. De machine wordt permanent verwijderd.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuleren</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteConfirm} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Verwijderen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
